import React, { useEffect, useState, useCallback } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  Text,
  TextInput,
  FormControl,
  Button,
  ButtonGroup,
  Select,
  Spinner,
} from "@primer/react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useCommonContext } from "../../contexts/common";
import { KyThiMonThiItemRequest } from "../../models/request/kythi-monthi/KyThiMonThiItemRequest";
import { hinhThucThiApi } from "../../api/hinhThucThiApi";
import { NotifyHelper } from "../../helpers/toast";
import { kyThiMonThiApi } from "../../api/kyThiMonThiApi";

interface KyThiMonThiDetailModalProps {
  id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface HinhThucThi {
  id: number;
  hinh_thuc: string;
}

const KyThiMonThiDetailModal: React.FC<KyThiMonThiDetailModalProps> = ({
  id,
  onSuccess,
  onCancel,
}) => {
  const { translate } = useCommonContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hinhThucThis, setHinhThucThis] = useState<HinhThucThi[]>([]);

  const defaultValues: KyThiMonThiItemRequest = {
    id: id,
    ex_kythi_id: 0,
    ex_monthi_id: 0,
    gio_bat_dau: null,
    gio_ket_thuc: null,
    thoi_luong_thi: null,
    ghi_chu: null,
    ex_hinhthucthi_id: 0,
    thu_tu: 0,
    list_monthi_id: [],
    gio_bat_dau_converted: "",
    gio_ket_thuc_converted: "",
    ngay_thi: null,
    ngay_thi_converted: "",
    thoi_luong_thi_converted: "",
  };

  const [formData, setFormData] =
    useState<KyThiMonThiItemRequest>(defaultValues);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<KyThiMonThiItemRequest>({
    defaultValues,
  });

  const loadHinhThucThis = async () => {
    try {
      setIsLoading(true);
      const res = await hinhThucThiApi.selectAll();
      if (res.is_success) {
        setHinhThucThis(res.data);
      } else {
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const loadDetail = useCallback(async () => {
    if (id > 0) {
      try {
        setIsLoading(true);
        const res = await kyThiMonThiApi.detail(id);
        console.log("Raw API Response:", res.data);

        if (res.is_success && res.data) {
          const formattedData = {
            ...res.data,
            ex_hinhthucthi_id: res.data.ex_hinhthucthi_id || 0,
          };

          // Convert string time to Date if available
          if (formattedData.gio_bat_dau_converted) {
            formattedData.gio_bat_dau = moment(
              formattedData.gio_bat_dau_converted,
              "HH:mm"
            ).toDate();
          }
          if (formattedData.gio_ket_thuc_converted) {
            formattedData.gio_ket_thuc = moment(
              formattedData.gio_ket_thuc_converted,
              "HH:mm"
            ).toDate();
          }
          if (formattedData.thoi_luong_thi_converted) {
            formattedData.thoi_luong_thi = moment(
              formattedData.thoi_luong_thi_converted,
              "HH:mm"
            ).toDate();
          }

          console.log("Formatted Data:", formattedData);
          setFormData(formattedData);

          // Update form values
          Object.keys(formattedData).forEach((key) => {
            setValue(key as keyof KyThiMonThiItemRequest, formattedData[key]);
          });
        }
      } catch (error) {
        console.error("Load detail error:", error);
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [id, setValue]);

  useEffect(() => {
    loadDetail();
    loadHinhThucThis();
  }, [loadDetail]);

  const validateForm = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return false;
    }

    if (!formData.gio_bat_dau && !formData.gio_bat_dau_converted) {
      NotifyHelper.Error(translate("Vui lòng nhập thời gian bắt đầu!"));
      return false;
    }

    if (!formData.gio_ket_thuc && !formData.gio_ket_thuc_converted) {
      NotifyHelper.Error(translate("Vui lòng nhập thời gian kết thúc!"));
      return false;
    }

    if (formData.ex_hinhthucthi_id === 0) {
      NotifyHelper.Error(translate("Vui lòng chọn hình thức thi!"));
      return false;
    }

    // Compare times using converted values
    const startTime = formData.gio_bat_dau_converted;
    const endTime = formData.gio_ket_thuc_converted;

    if (startTime && endTime && startTime >= endTime) {
      NotifyHelper.Error(
        translate("Thời gian kết thúc phải lớn hơn thời gian bắt đầu!")
      );
      return false;
    }

    return true;
  };

  const onSubmit = async (data: KyThiMonThiItemRequest) => {
    if (!(await validateForm())) {
      return;
    }

    setIsSaving(true);
    try {
      const api = id > 0 ? kyThiMonThiApi.update : kyThiMonThiApi.insert;
      const res = await api(formData);

      if (res.is_success) {
        NotifyHelper.Success(
          translate(id > 0 ? "Success.Update" : "Success.Create")
        );
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      NotifyHelper.Error(translate("Error.Generic"));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog
        title={translate(id > 0 ? "Cập nhật" : "Thêm mới")}
        onClose={onCancel}
        width="medium"
      >
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Spinner size="large" />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={translate(id > 0 ? "Cập nhật" : "Thêm mới")}
      onClose={onCancel}
      width="large"
    >
      <Box as="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 3,
            mb: 3,
          }}
        >
          <FormControl required>
            <FormControl.Label>{translate("Hình thức thi")}</FormControl.Label>
            <Select
              {...register("ex_hinhthucthi_id", {
                required: translate("Validation.Required"),
                min: { value: 1, message: translate("Validation.Required") },
              })}
              value={formData.ex_hinhthucthi_id.toString()}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                setFormData((prev) => ({
                  ...prev,
                  ex_hinhthucthi_id: value,
                }));
                setValue("ex_hinhthucthi_id", value);
              }}
            >
              <Select.Option value="0">-- Select --</Select.Option>
              {hinhThucThis.map((item) => (
                <Select.Option key={item.id} value={item.id.toString()}>
                  {item.hinh_thuc}
                </Select.Option>
              ))}
            </Select>
            {errors.ex_hinhthucthi_id && (
              <FormControl.Validation variant="error">
                {errors.ex_hinhthucthi_id.message}
              </FormControl.Validation>
            )}
          </FormControl>

          <FormControl>
            <FormControl.Label>{translate("Ngày thi")}</FormControl.Label>
            <TextInput value={formData.ngay_thi_converted || ""} disabled />
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 3,
            mb: 3,
          }}
        >
          <FormControl required>
            <FormControl.Label>{translate("Bắt đầu")}</FormControl.Label>
            <TextInput
              type="time"
              {...register("gio_bat_dau", {
                required: translate("Validation.Required"),
              })}
              value={formData.gio_bat_dau_converted || ""}
              onChange={(e) => {
                const timeValue = e.target.value;
                const newDate = moment(timeValue, "HH:mm").toDate();

                setFormData((prev) => ({
                  ...prev,
                  gio_bat_dau: newDate,
                  gio_bat_dau_converted: timeValue,
                }));
                setValue("gio_bat_dau", newDate);
                setValue("gio_bat_dau_converted", timeValue);
              }}
            />
            {errors.gio_bat_dau && (
              <FormControl.Validation variant="error">
                {errors.gio_bat_dau.message}
              </FormControl.Validation>
            )}
          </FormControl>

          <FormControl required>
            <FormControl.Label>{translate("Kết thúc")}</FormControl.Label>
            <TextInput
              type="time"
              {...register("gio_ket_thuc", {
                required: translate("Validation.Required"),
              })}
              value={formData.gio_ket_thuc_converted || ""}
              onChange={(e) => {
                const timeValue = e.target.value;
                const newDate = moment(timeValue, "HH:mm").toDate();

                setFormData((prev) => ({
                  ...prev,
                  gio_ket_thuc: newDate,
                  gio_ket_thuc_converted: timeValue,
                }));
                setValue("gio_ket_thuc", newDate);
                setValue("gio_ket_thuc_converted", timeValue);
              }}
            />
            {errors.gio_ket_thuc && (
              <FormControl.Validation variant="error">
                {errors.gio_ket_thuc.message}
              </FormControl.Validation>
            )}
          </FormControl>
        </Box>

        <FormControl sx={{ mb: 3, width: "100%" }}>
          <FormControl.Label>{translate("Ghi chú")}</FormControl.Label>
          <TextInput
            block // This prop makes the input take full width
            {...register("ghi_chu")}
            value={formData.ghi_chu || ""}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                ghi_chu: e.target.value,
              }));
              setValue("ghi_chu", e.target.value);
            }}
          />
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <ButtonGroup>
            <Button onClick={onCancel}>{translate("Base.Label.Cancel")}</Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving && <Spinner size="small" sx={{ mr: 2 }} />}
              {translate(id > 0 ? "Base.Label.Update" : "Base.Label.Create")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Dialog>
  );
};

export default KyThiMonThiDetailModal;
